/**
 * As fotos do feedback, preparadas no navegador antes de subir.
 *
 * O limite da API é 5 MB por foto — e um print de tela 4K em PNG passa disso
 * fácil. Recusar seria punir justamente quem se deu ao trabalho de mostrar o
 * problema; então a foto grande é reduzida aqui, e só a que continua grande
 * depois disso é recusada.
 */

import { LIMITES } from "@/api/feedbackDoSistema"

const ACEITOS = new Set(["image/png", "image/jpeg", "image/webp"])

/** Maior lado depois da redução. Legível até em print de tela cheia. */
const LADO_MAXIMO = 2560

export class ImagemRecusada extends Error {}

export async function prepararImagem(arquivo: File): Promise<File> {
  if (!ACEITOS.has(arquivo.type)) {
    throw new ImagemRecusada(
      `"${arquivo.name}" não é JPG, PNG ou WEBP${arquivo.type ? ` (${arquivo.type})` : ""}.`,
    )
  }

  let imagem: ImageBitmap
  try {
    imagem = await createImageBitmap(arquivo)
  } catch {
    throw new ImagemRecusada(`Não consegui abrir "${arquivo.name}" como imagem.`)
  }

  try {
    const escala = Math.min(1, LADO_MAXIMO / Math.max(imagem.width, imagem.height))
    if (escala === 1 && arquivo.size <= LIMITES.bytesPorImagem) return arquivo
    return await paraJpeg(imagem, imagem.width, imagem.height, arquivo.name)
  } finally {
    imagem.close()
  }
}

/**
 * Desenha a imagem no tamanho final e codifica em JPEG — uma codificação só.
 * Baixa a qualidade em degraus até caber nos 5 MB.
 */
async function paraJpeg(fonte: CanvasImageSource, largura: number, altura: number, nome: string) {
  const escala = Math.min(1, LADO_MAXIMO / Math.max(largura, altura))
  const l = Math.round(largura * escala)
  const a = Math.round(altura * escala)

  const tela = document.createElement("canvas")
  tela.width = l
  tela.height = a
  const ctx = tela.getContext("2d")!
  // JPEG não tem transparência: sem o fundo branco, o que era transparente no
  // PNG vira preto e o print fica ilegível.
  ctx.fillStyle = "#FFFFFF"
  ctx.fillRect(0, 0, l, a)
  ctx.drawImage(fonte, 0, 0, l, a)

  for (const qualidade of [0.9, 0.8, 0.68]) {
    const blob = await new Promise<Blob | null>((ok) => tela.toBlob(ok, "image/jpeg", qualidade))
    if (blob && blob.size <= LIMITES.bytesPorImagem) {
      return new File([blob], nome.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg", lastModified: Date.now() })
    }
  }

  throw new ImagemRecusada(`"${nome}" continua acima de 5 MB mesmo reduzida.`)
}

/** O navegador sabe capturar a tela? (Chrome, Edge e Firefox no computador.) */
export const podeCapturarTela =
  typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia

/** Um quadro da tela, ainda sem codificar — ver `capturarQuadro`. */
export type Quadro = { fonte: ImageBitmap | HTMLVideoElement; largura: number; altura: number; liberar: () => void }

/**
 * Um quadro da tela, pela API do próprio navegador — sem biblioteca.
 *
 * O navegador pergunta o que compartilhar; o Chrome já oferece "esta aba"
 * primeiro por causa do `preferCurrentTab`. **A captura termina assim que o
 * quadro chega**, e a codificação fica para `quadroParaArquivo`: quem chama
 * pode devolver o painel à tela entre uma coisa e outra, e a pessoa não fica
 * olhando para o nada enquanto o JPEG é gerado.
 *
 * O que deixava a primeira versão lenta, e não existe mais aqui: uma espera
 * fixa de 250 ms "para a tela assentar", o quadro inteiro codificado em PNG (o
 * passo mais caro, numa tela de 2880 px), e depois decodificado e recodificado
 * em JPEG. Agora o quadro vem na resolução já limitada pelo próprio capturador
 * e é codificado uma vez só.
 */
export async function capturarQuadro(): Promise<Quadro> {
  const fluxo = await navigator.mediaDevices.getDisplayMedia({
    video: {
      displaySurface: "browser",
      // O capturador já entrega reduzido: menos pixel para copiar e codificar.
      width: { max: LADO_MAXIMO },
      height: { max: LADO_MAXIMO },
      frameRate: { max: 5 },
    },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: "include",
    surfaceSwitching: "exclude",
  } as DisplayMediaStreamOptions)

  const trilha = fluxo.getVideoTracks()[0]!
  const parar = () => fluxo.getTracks().forEach((t) => t.stop())

  // Caminho rápido (Chrome e Edge): o quadro sai direto da trilha, sem <video>.
  const Captura = (window as unknown as { ImageCapture?: new (t: MediaStreamTrack) => { grabFrame(): Promise<ImageBitmap> } }).ImageCapture
  if (Captura) {
    try {
      const bitmap = await new Captura(trilha).grabFrame()
      parar()
      return { fonte: bitmap, largura: bitmap.width, altura: bitmap.height, liberar: () => bitmap.close() }
    } catch {
      // Cai para o <video> abaixo — o fluxo continua aberto.
    }
  }

  // Firefox: um <video> e o primeiro quadro que ele de fato pintar.
  const video = document.createElement("video")
  video.srcObject = fluxo
  video.muted = true
  video.playsInline = true
  await video.play()
  await new Promise<void>((ok) => {
    const v = video as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => void }
    if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(() => ok())
    else if (video.readyState >= 2) ok()
    else video.addEventListener("loadeddata", () => ok(), { once: true })
  })

  return {
    fonte: video,
    largura: video.videoWidth,
    altura: video.videoHeight,
    liberar: () => { parar(); video.srcObject = null },
  }
}

/** O quadro capturado, como JPEG pronto para anexar. */
export async function quadroParaArquivo(q: Quadro): Promise<File> {
  try {
    const agora = new Date()
    const carimbo = agora.toISOString().slice(0, 19).replace(/[-:]/g, "").replace("T", "_")
    return await paraJpeg(q.fonte, q.largura, q.altura, `tela_${carimbo}.jpg`)
  } finally {
    q.liberar()
  }
}

export function tamanhoLegivel(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
